import { VegaButton, VegaButtonLink, VegaFlex, VegaFont, VegaInput } from '@globalpayments/vega-react';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { isEmpty } from 'lodash';

import apiFunctions from '../../api';

import { Pages } from '../../types/pages';
import { requestLogin, tooManyRequests } from '../../types/api';
import { useAuth } from '../../utility/auth';
import { LastLoginUsernameKey, UnauthorizedErrorKey } from '../../types/localStorage';

import { RetrieveFromLocalStorage, InsertToLocalStorage, DeleteFromLocalStorage } from '../../utility/localStorage';
import PageSkeleton from '../../components/pageSkeleton';
import authStyles from '../auth.module.css';
import styles from './index.module.css';

interface LoginQuote {
  attribution: string;
  text: string;
}

const Login = () => {
  const auth = useAuth();
  const location: any = useLocation();
  const usernameInputRef = useRef<HTMLVegaInputElement>(null);

  const [username, setUsername] = useState<string>(RetrieveFromLocalStorage(LastLoginUsernameKey) || '');
  const [errorUsername, setErrorUsername] = useState<boolean>(false);
  const [tooMany, setTooMany] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [sentCode, setSentCode] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(RetrieveFromLocalStorage(UnauthorizedErrorKey));

  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    let subscribed = true;
    const requestLink = async () => {
      await apiFunctions.apiPost(requestLogin, { username: username }).then((res) => {
        if (subscribed) {
          if (res === tooManyRequests) {
            setTooMany(true);
          } else {
            setSentCode(true);
            setErrorMessage('');
            DeleteFromLocalStorage(UnauthorizedErrorKey);
            InsertToLocalStorage(LastLoginUsernameKey, username);
          }

          setLoading(false);
        }
      });
    };

    if (loading) {
      requestLink();
    }

    return () => {
      subscribed = false;
    };
  }, [loading, auth, username]);

  useEffect(() => {
    void usernameInputRef.current?.doFocus();
  }, []);

  const handleLoginLinkRequest = () => {
    setErrorUsername(false);
    let user = username.trim();
    if (user.indexOf('@e-hps.com') !== -1) {
      user = user.substring(0, user.indexOf('@e-hps.com'));
      setUsername(user);
    }
    if (user.length > 0 && user.length < 100 && !isEmpty(user)) {
      setLoading(true);
    } else setErrorUsername(true);
  };

  if (loading) return <PageSkeleton label="Requesting login code" variant="form" />;
  else if (sentCode) return <Navigate to={Pages.Start} state={{ sent: true, from: from }}></Navigate>;
  else if (!auth.user)
    return (
      <VegaFlex alignItems="center" className={authStyles.form} direction="col" gap="size-16" justifyContent="center">
        <VegaInput
          autoValidation={false}
          className={authStyles.input}
          id="Username"
          isValid={!errorUsername}
          label="Username"
          maxLength={100}
          onKeyDown={(event) => {
            if (event.code === 'Enter') handleLoginLinkRequest();
          }}
          onVegaChange={(event) => setUsername(event.detail)}
          ref={usernameInputRef}
          value={username}
          valueUpdateTrigger="input"
        />
        {errorUsername && (
          <VegaFont as="p" color="text-danger-link" variant="font-footnote1-short">
            Invalid input.
          </VegaFont>
        )}
        <VegaButton
          disabled={tooMany}
          id="CodeRequestButton"
          label="Request Code"
          onVegaClick={handleLoginLinkRequest}
        />
        <VegaButtonLink label="Already have a code?" link={Pages.Start} />
        {tooMany && (
          <VegaFont as="p" color="text-danger-link" variant="font-p2-short">
            Too many requests
          </VegaFont>
        )}
        {errorMessage && (
          <VegaFont as="p" color="text-danger-link" variant="font-p2-short">
            {errorMessage}
          </VegaFont>
        )}
      </VegaFlex>
    );
  else return <Navigate to={from} />;
};

export default Login;
